import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FaInstagram, FaLinkedin, FaFacebook, FaUserCheck, FaMoneyBillWave, FaTools, FaBalanceScale, FaBullhorn, FaChartLine } from 'react-icons/fa';
import heroVideo from '../assets/videos/sell-hero.mp4';

const servicesIncluded = [
  {
    title: 'Tenant Sourcing & Screening',
    desc: 'Comprehensive tenant background checks, credit verification, and rental history analysis.',
    icon: <FaUserCheck className="w-12 h-12 text-accent" />
  },
  {
    title: 'Rent Collection & Management',
    desc: 'Automated rent collection, late payment handling, and financial reporting.',
    icon: <FaMoneyBillWave className="w-12 h-12 text-accent" />
  },
  {
    title: 'Property Maintenance',
    desc: '24/7 maintenance coordination, regular inspections, and preventive care.',
    icon: <FaTools className="w-12 h-12 text-accent" />
  },
  {
    title: 'Legal Compliance',
    desc: 'RERA compliance, contract management, and legal documentation handling.',
    icon: <FaBalanceScale className="w-12 h-12 text-accent" />
  },
  {
    title: 'Marketing & Advertising',
    desc: 'Professional photography, virtual tours, and multi-platform property marketing.',
    icon: <FaBullhorn className="w-12 h-12 text-accent" />
  },
  {
    title: 'Financial Reporting',
    desc: 'Monthly statements, expense tracking, and tax documentation support.',
    icon: <FaChartLine className="w-12 h-12 text-accent" />
  }
];

const landlordTestimonials = [
  {
    name: 'David Thompson',
    location: 'Dubai Marina',
    testimonial: 'Alpha Ahead has managed my properties for 3 years. Their professional service and attention to detail have maximized my rental income while minimizing stress.',
    rating: 5,
    img: 'https://randomuser.me/api/portraits/men/32.jpg'
  },
  {
    name: 'Fatima Al Rashid',
    location: 'Dubai Hills Estate',
    testimonial: 'As an overseas owner, I needed reliable property management. Alpha Ahead handles everything perfectly and keeps me updated regularly.',
    rating: 5,
    img: 'https://randomuser.me/api/portraits/women/44.jpg'
  },
  {
    name: 'Michael Chen',
    location: 'The Valley',
    testimonial: 'Excellent service from start to finish. They found quality tenants quickly and maintain my property to the highest standards.',
    rating: 5,
    img: 'https://randomuser.me/api/portraits/men/35.jpg'
  }
];

function PropertyManagementPage() {
  return (
    <div className="flex flex-col min-h-screen font-sans bg-white">
      <Navbar />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
          <video
            className="absolute inset-0 w-full h-full object-cover z-0"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          >
            <source src={heroVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-black/60 z-0" />
          <div className="relative z-10 max-w-4xl mx-auto text-center px-4 py-16">
            <h1 className="text-4xl md:text-6xl font-light text-white mb-6 drop-shadow-lg" style={{ fontFamily: 'Roboto, sans-serif' }}>
              Comprehensive Property Management
            </h1>
            <p className="text-xl text-white/90 mb-8 drop-shadow max-w-3xl mx-auto leading-relaxed">
              We provide end-to-end property management for local and international property owners. From tenant sourcing and rent collection to maintenance and legal compliance, we ensure peace of mind and steady returns.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#services" className="px-8 py-4 rounded-lg bg-accent text-white font-semibold text-lg hover:bg-accent/90 transition-colors duration-200">
                View Services
              </a>
              <a
                href="https://wa.me/971505606877?text=I%20would%20like%20to%20get%20a%20quote%20for%20property%20management."
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 rounded-lg bg-transparent text-white font-semibold text-lg border-2 border-white/40 hover:bg-white hover:text-primary transition-colors duration-200"
              >
                Get Quote
              </a>
            </div>
          </div>
        </section>

        {/* Services Included */}
        <section id="services" className="max-w-6xl mx-auto py-20 px-4">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-12 text-center" style={{ fontFamily: 'Roboto, sans-serif' }}>
            Services Included
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesIncluded.map((service, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center">
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Peace of Mind Section (Replaces Owner Portal) */}
        <section className="max-w-7xl mx-auto py-20 px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="relative h-[400px] md:h-[500px] rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/communities/Dubai Marina.png"
                alt="Property Management Excellence"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6" style={{ fontFamily: 'Roboto, sans-serif' }}>
                Experience True Peace of Mind
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our dedicated team handles every aspect of your investment, ensuring your property is well-maintained and your tenants are happy. We treat your property as if it were our own.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center text-gray-700">
                  <FaUserCheck className="text-accent mr-3 w-5 h-5" />
                  <span>Rigorous tenant screening process</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <FaTools className="text-accent mr-3 w-5 h-5" />
                  <span>Proactive maintenance & inspections</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <FaChartLine className="text-accent mr-3 w-5 h-5" />
                  <span>Transparent financial reporting</span>
                </li>
              </ul>
              <a
                href="https://wa.me/971505606877?text=I%20am%20interested%20in%20partnering%20with%20you%20for%20property%20management."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-8 py-3 rounded-lg bg-accent text-white font-semibold hover:bg-accent/90 transition-colors"
              >
                Partner With Us
              </a>
            </div>
          </div>
        </section>

        {/* Testimonials from Landlords */}
        <section className="max-w-6xl mx-auto py-20 px-4 bg-gray-50 rounded-3xl mb-20">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-12 text-center" style={{ fontFamily: 'Roboto, sans-serif' }}>
            What Our Landlords Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {landlordTestimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  <img src={testimonial.img} alt={testimonial.name} className="w-12 h-12 rounded-full mr-4" />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.location}</p>
                  </div>
                </div>
                <p className="text-gray-600 italic">"{testimonial.testimonial}"</p>
                <div className="flex text-yellow-400 mt-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 px-4">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0"
            style={{
              backgroundImage: `url('/images/communities/Rashid Yacht & Marina.png')`,
            }}
          />
          <div className="absolute inset-0 bg-black/70 z-0" />
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6" style={{ fontFamily: 'Roboto, sans-serif' }}>
              Ready to Maximize Your Property Returns?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let our professional team handle your property management while you enjoy passive income and peace of mind.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/971505606877?text=I%20want%20to%20get%20started%20with%20your%20property%20management%20services."
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 rounded-lg bg-accent text-white font-semibold text-lg hover:bg-accent/90 transition-colors duration-200"
              >
                Get Started Today
              </a>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+971505606877" className="px-8 py-4 rounded-lg bg-transparent text-white font-semibold text-lg border-2 border-white/40 hover:bg-white hover:text-primary transition-colors duration-200">
                  Call Mobile
                </a>
                <a href="tel:+97144323080" className="px-8 py-4 rounded-lg bg-transparent text-white font-semibold text-lg border-2 border-white/40 hover:bg-white hover:text-primary transition-colors duration-200">
                  Call Landline
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default PropertyManagementPage; 